import { selectSyncing, setSyncing } from '@/redux/config/configSlice';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import toaster from '@/toaster';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useCustomersSetter } from './customers/useCustomersQueries';
import { useBusinessSetter } from './user/useBusinessData';
import { useCurrencySetter } from './user/useCurrency';
import { useDataStoreSetter } from './user/useData';
import { useInovoicesStoreSetter } from './user/useInvoices';
import { usePinSetter } from './user/usePin';

const AUTO_SYNC_INTERVAL = 5 * 60 * 1000; // 5 minutes in milliseconds

export const useSync = () => {
  const { t } = useTranslation(['common']);
  const dispatch = useAppDispatch();
  const syncTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const syncing = useAppSelector(selectSyncing);
  const { isAuthenticated } = useAuth();

  const { refetch: syncData } = useDataStoreSetter();
  const { refetch: syncInvoices } = useInovoicesStoreSetter();
  const { refetch: syncBusiness } = useBusinessSetter();
  const { refetch: syncCurrency } = useCurrencySetter();
  const { refetch: syncPin } = usePinSetter();
  const { refetch: syncCustomers } = useCustomersSetter();

  const startSync = useCallback(() => {
    if (!isAuthenticated) {
      return; // Don't sync if not authenticated
    }

    if (navigator.onLine) {
      dispatch(setSyncing(true));
    } else {
      toaster.error(t('common:no_internet_connection'));
    }
  }, [dispatch, t, isAuthenticated]);

  const syncAll = useCallback(async () => {
    if (!syncing || !isAuthenticated) return;
    try {
      await Promise.all([
        syncData(),
        syncCurrency(),
        syncBusiness(),
        syncInvoices(),
        syncPin(),
        syncCustomers(),
      ]);
      toaster.success(t('common:synced_successfully'));
    } catch (error) {
      toaster.error(t('common:sync_failed'));
      console.error('Sync failed:', error);
    } finally {
      dispatch(setSyncing(false));
    }
  }, [
    dispatch,
    syncBusiness,
    syncCurrency,
    syncData,
    syncInvoices,
    syncPin,
    syncCustomers,
    t,
    syncing,
    isAuthenticated,
  ]);

  const scheduleNextSync = useCallback(() => {
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    syncTimeoutRef.current = setTimeout(() => {
      if (navigator.onLine && isAuthenticated) {
        startSync();
      }
      scheduleNextSync();
    }, AUTO_SYNC_INTERVAL);
  }, [startSync, isAuthenticated]);

  // Handle auto sync scheduling
  useEffect(() => {
    if (isAuthenticated) {
      scheduleNextSync();
    }

    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, [scheduleNextSync, isAuthenticated]);

  // Run syncAll when syncing state changes to true
  useEffect(() => {
    if (syncing && isAuthenticated) {
      syncAll();
    }
  }, [syncing, syncAll, isAuthenticated]);

  return {
    startSync,
    syncAll,
    isOnline: navigator.onLine,
  };
};
